import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { confirmOwnerCheckout } from '../utils/livestreamApi';
import { fetchLivestreams } from '../store/LivestreamSlice';
import styled from 'styled-components';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f9f9f9;
  padding: 2rem;
`;

const Card = styled.div`
  background: white;
  border-radius: 1rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  padding: 3rem;
  max-width: 500px;
  width: 100%;
  text-align: center;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #010101;
  margin-bottom: 1rem;
`;

const Message = styled.p`
  font-size: 1.125rem;
  color: #6b7280;
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const ErrorMessage = styled.p`
  font-size: 1.125rem;
  color: #dc2626;
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const LoadingMessage = styled.p`
  font-size: 1.125rem;
  color: #6b7280;
  margin-bottom: 2rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const Button = styled(Link)`
  display: inline-block;
  padding: 0.75rem 1.5rem;
  background: #BD2026;
  color: white;
  text-decoration: none;
  border-radius: 0.5rem;
  font-weight: 600;
  transition: all 0.2s;

  &:hover {
    background: #010101;
    transform: translateY(-1px);
  }
`;

const SecondaryButton = styled(Link)`
  display: inline-block;
  padding: 0.75rem 1.5rem;
  background: #f3f4f6;
  color: #374151;
  text-decoration: none;
  border-radius: 0.5rem;
  font-weight: 600;
  transition: all 0.2s;

  &:hover {
    background: #e5e7eb;
    transform: translateY(-1px);
  }
`;

const SuccessIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
`;

function OwnerPaymentSuccess() {
  const { livestreamId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('Verifying owner payment...');
  const [error, setError] = useState(null);
  const [livestream, setLivestream] = useState(null);

  useEffect(() => {
    const confirmPayment = async () => {
      try {
        // Extract session_id from URL
        const urlParams = new URLSearchParams(window.location.search);
        const sessionId = urlParams.get('session_id');
        
        if (!sessionId) {
          setError('No payment session found in URL');
          setLoading(false);
          return;
        }

        // Confirm the owner payment with backend
        const response = await confirmOwnerCheckout(sessionId);
        
        // Handle ApiEnvelope response structure
        const data = response?.data?.data || response?.data || response;
        
        if (data === 'CONFIRMED' || data?.success) {
          setStatus('Owner payment confirmed! Your livestream is now active.');
          
          // Refresh livestreams to show updated status
          dispatch(fetchLivestreams());
          
          // Clean up URL to remove session_id
          const newUrl = new URL(window.location);
          newUrl.searchParams.delete('session_id');
          window.history.replaceState({}, '', newUrl);
          
        } else {
          setError('Payment confirmation failed. Please contact support.');
        }
      } catch (err) {
        console.error('[OwnerPaymentSuccess] Payment confirmation error:', err);
        setError('Error confirming payment. Please try again or contact support.');
      } finally {
        setLoading(false);
      }
    };

    confirmPayment();
  }, [livestreamId, dispatch]);

  return (
    <Container>
      <Card>
        {loading ? (
          <>
            <LoadingMessage>{status}</LoadingMessage>
            <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
          </>
        ) : error ? (
          <>
            <SuccessIcon>❌</SuccessIcon>
            <Title>Payment Error</Title>
            <ErrorMessage>{error}</ErrorMessage>
            <ButtonGroup>
              <Button to="/livestream/manage">Manage Livestreams</Button>
              <SecondaryButton to="/dashboard">Dashboard</SecondaryButton>
            </ButtonGroup>
          </>
        ) : (
          <>
            <SuccessIcon>✅</SuccessIcon>
            <Title>Payment Successful!</Title>
            <Message>{status}</Message>
            <ButtonGroup>
              {livestreamId && (
                <Button to={`/livestream/watch/${livestreamId}`}>
                  Start Your Livestream
                </Button>
              )}
              <Button to="/livestream/manage">Manage Livestreams</Button>
              <SecondaryButton to="/dashboard">Dashboard</SecondaryButton>
            </ButtonGroup>
          </>
        )}
      </Card>
    </Container>
  );
}

export default OwnerPaymentSuccess;

